import { makeMPServerRequest } from "../assets/helpers";

import { SessionWithOrdersAndType } from "@/types/session-interfaces";
import { Folder, FolderWithSessions, FoldersWithPagination } from "@/types/folder-interfaces";

/**
 * Creates a new folder with the specified title.
 *
 * Sends a POST request to the MP server to create the folder.
 *
 * @param {string} title - The title of the folder to create.
 * @returns {Promise<Folder>} A promise that resolves to the created Folder object.
 * @throws {Error} If the request fails or returns an unexpected response.
 */
export async function createFolder(title: string): Promise<FolderWithSessions> {
  try {
    const mpResponse = await makeMPServerRequest(
      "self/folders",
      "POST",
      "json",
      JSON.stringify({title}),
    );

    if (mpResponse.status === "success") {
      return mpResponse.data as FolderWithSessions;
    } else {
      throw new Error(
        `Unexpected response while creating folder name "${title}": ${JSON.stringify(mpResponse)}`,
      );
    }
  } catch (error:any) {
    throw new Error(`Error creating folder "${title}": ${error.message}`);
  }
}

/**
 * Updates an existing folder with the provided data.
 *
 * Sends a PUT request to update folder attributes.
 *
 * @param {Partial<Folder>} folderUpdateData - Partial data to update the folder.
 * @returns {Promise<Folder>} A promise that resolves to the updated Folder object.
 * @throws {Error} If the request fails or returns an unexpected response.
 */
export async function updateFolder(
  folderId: string,
  folderUpdateData: Partial<Folder>,
): Promise<Folder> {
  try {
    const mpResponse = await makeMPServerRequest(
      `self/folders/${folderId}`,
      "PUT",
      "json",
      JSON.stringify(folderUpdateData),
    );

    if (mpResponse.status === "success") {
      return mpResponse.data as Folder;
    } else {
      throw new Error(
        `Unexpected response while updating folder: ${JSON.stringify(mpResponse)}`,
      );
    }
  } catch (error) {
    throw new Error(
      `Unexpected exception while updating folder: ${error}`
    );
  }
}

/**
 * Deletes (soft delete) a folder by its ID.
 *
 * Sends a PUT request to mark the folder as deleted.
 *
 * @param {string} folderId - The ID of the folder to delete.
 * @returns {Promise<Folder>} A promise that resolves to the deleted Folder object.
 * @throws {Error} If the request fails or returns an unexpected response.
 */
export async function deleteFolder(folderId: string): Promise<Folder> {
  try {
    const mpResponse = await makeMPServerRequest(
      `self/folders/${folderId}/delete`,
      "PUT",
      "json",
    );

    if (mpResponse.status === "success") {
      return mpResponse.data as Folder;
    } else {
      throw new Error(
        `Unexpected response while deleting folder "${folderId}": ${JSON.stringify(mpResponse)}`,
      );
    }
  } catch (error) {
    throw new Error(
      `Unexpected exception while deleting folder name "${folderId}": ${error}`
    );
  }
}

/**
 * Restores a previously deleted folder by its ID.
 *
 * Sends a PUT request to undelete the folder.
 *
 * @param {string} folderId - The ID of the folder to restore.
 * @returns {Promise<Folder>} A promise that resolves to the restored Folder object.
 * @throws {Error} If the request fails or returns an unexpected response.
 */
export async function restoreFolder(folderId: string): Promise<Folder> {
  try {
    const mpResponse = await makeMPServerRequest(
      `self/folders/${folderId}/undelete`,
      "PUT",
      "json",
    );
    
    if (mpResponse.status === "success") {
      return mpResponse.data as Folder;
    } else {
      throw new Error(
        `Unexpected response while restoring folder "${folderId}": ${JSON.stringify(mpResponse)}`,
      );
    }
  } catch (error) {
    throw new Error(
      `Unexpected exception while restoring folder name "${folderId}": ${error}`
    );
  }
}

/**
 * Archive an existing folder by its ID.
 *
 * Sends a POST request to archive the folder.
 *
 * @param {string} folderId - The ID of the folder to archive.
 * @returns {Promise<Folder>} A promise that resolves to the archived Folder object.
 * @throws {Error} If the request fails or returns an unexpected response.
 */
export async function archiveFolder(folderId: string): Promise<Folder> {
  try {
    const mpResponse = await makeMPServerRequest(
      `self/folders/${folderId}/archive`,
      "PUT",
      "json",
    );

    if (mpResponse.status === "success") {
      return mpResponse.data as Folder;
    } else {
      throw new Error(
        `Unexpected response while archiving folder "${folderId}": ${JSON.stringify(mpResponse)}`,
      );
    }
  } catch (error) {
    throw new Error(
      `Unexpected exception while archiving folder "${folderId}": ${error}`
    );
  }
}

/**
 * Unarchives a previously archived folder by its ID.
 *
 * Sends a POST request to the MP server to move the folder back into the active state.
 *
 * @param {string} folderId - The unique identifier of the folder to unarchive.
 * @returns {Promise<Folder>} A promise that resolves to the unarchived Folder object.
 * @throws {Error} If the request fails or returns an unexpected response.
 */
export async function unarchiveFolder(folderId: string): Promise<Folder> {
  try {
    const mpResponse = await makeMPServerRequest(
      `self/folders/${folderId}/unarchive`,
      "PUT",
      "json",
    );

    if (mpResponse.status === "success") {
      return mpResponse.data as Folder;
    } else {
      throw new Error(
        `Unexpected response while unarchiving folder "${folderId}": ${JSON.stringify(mpResponse)}`,
      );
    }
  } catch (error) {
    throw new Error(
      `Unexpected exception while unarchiving folder "${folderId}": ${error}`
    );
  }
}

/**
 * Checks whether a folder title is available (i.e., not already in use).
 *
 * Sends a GET request to fetch all folders, then compares titles to determine uniqueness.
 *
 * @param {string} title - The folder title to check.
 * @returns {Promise<boolean>} A promise that resolves to true if the title is unique, false if it already exists.
 * @throws {Error} If the request fails or returns an unexpected response.
 */
export async function checkFolderName(title: string): Promise<boolean> {
  try {
    const mpResponse = await makeMPServerRequest(
      `self/folders`,
      "GET",
      "json",
    );

    if (mpResponse.status !== "success") {
      throw new Error(
        `Unexpected response while getting all folders: ${JSON.stringify(mpResponse)}`,
      );
    }

    const folderList = mpResponse.data as Folder[];
    const isUnique = !folderList.some(folder => folder.title === title);

    return isUnique;
  } catch (error) {
    throw new Error(
      `Unexpected error while checking folder name "${title}": ${error}`,
    );
  }
}

/**
 * Retrieves a list of folders from the server.
 *
 * This function makes a GET request to the server endpoint to fetch a list of folders. 
 * If the request is successful, it returns the folder data as an array of `Folder` objects.
 * In case of an unexpected response or failure, it throws an error with details about the issue.
 *
 * @async
 * @function getFolderList
 * @returns {Promise<Folder[]>} A promise that resolves to an array of `Folder` objects.
 * @throws {Error} Throws an error if the server response is unexpected or if an exception occurs during the request.
 */
export async function getFolderList(): Promise<Folder[]> {
  try {
    const mpResponse = await makeMPServerRequest(
      "self/folders",
      "GET",
      "json",
    );

    if (mpResponse.status === "success") {
      return mpResponse.data as Folder[];
    } else {
      throw new Error(
        `Unexpected response while getting folder list: ${JSON.stringify(mpResponse)}`,
      );
    }
  } catch (error) {
    throw new Error(
      `Unexpected exception while getting folder list: ${error}`
    );
  }
}

/**
 * Attaches sessions to corresponding folders and returns an object containing 
 * the updated folders with associated sessions and a list of sessions without a folder.
 * 
 * This function groups sessions by their folderId, then for each folder, it associates 
 * the relevant sessions. It also categorizes sessions that do not have a folderId into 
 * a separate list. Each folder is returned with its associated sessions and a type property.
 * 
 * @param {Folder[]} folders - An array of folders to which sessions will be attached.
 * @param {SessionWithOrdersAndType[]} sessions - An array of sessions to be grouped by folderId.
 * @returns {Object} - An object containing:
 *   - `folders`: An array of folders, each enhanced with:
 *     - `session`: The list of sessions associated with the folder (if any).
 *     - `type`: The value `"folder"`, indicating the type of the folder.
 *   - `noFolderSessions`: An array of sessions that do not have a folderId.
 */
export function attachSessionsToFolders(
  folders: Folder[],
  sessions: SessionWithOrdersAndType[],
) {
  const sessionMap = new Map<string, SessionWithOrdersAndType[]>();
  const noFolderSessions = [];

  for (const session of sessions) {
    if (session.folderId === null) {
      noFolderSessions.push(session)
      continue
    }
    if (!sessionMap.has(session.folderId)) {
      sessionMap.set(session.folderId, []);
    }
    sessionMap.get(session.folderId)!.push(session);
  }

  return {
    folders: folders.map(folder => ({
      ...folder,
      sessions: sessionMap.get(folder.id) || [],
      type: 'folder',
    })),
    noFolderSessions,
  }
  
}


/**
 * Retrieves a folder object from the server.
 *
 * This function makes a GET request to the server endpoint to fetch a folder object. 
 * If the request is successful, it returns the folder data a `Folder` object.
 * In case of an unexpected response or failure, it throws an error with details about the issue.
 *
 * @async
 * @function getFolder
 * @returns {Promise<Folder>} A promise that resolves to a `Folder` object.
 * @throws {Error} Throws an error if the server response is unexpected or if an exception occurs during the request.
 */
export async function getFolder(folderId: string): Promise<FolderWithSessions> {
  try {
    const mpResponse = await makeMPServerRequest(
      `self/folders/${folderId}/detail`,
      "GET",
      "json",
    );

    if (mpResponse.status === "success") {
      return mpResponse.data as FolderWithSessions;
    } else {
      throw new Error(
        `Unexpected response while getting a folder obj: ${JSON.stringify(mpResponse)}`,
      );
    }
  } catch (error) {
    throw new Error(
      `Unexpected exception while getting a folder obj: ${error}`
    );
  }
}


export async function getFolderListWithSessions(filterQuery?: string): Promise<Folder[]> {
  try {

    const endpoint = filterQuery 
    ? `self/folders/list?${filterQuery}` 
    : "self/folders/list";

    const mpResponse = await makeMPServerRequest(
      endpoint,
      "GET",
      "json",
    );

    if (mpResponse.status === "success") {
      return mpResponse.data as FolderWithSessions[];      
    } else {
      throw new Error(
        `Unexpected response while getting folder list: ${JSON.stringify(mpResponse)}`,
      );
    }
  } catch (error) {
    throw new Error(
      `Unexpected exception while getting folder list: ${error}`
    );
  }
}

export async function getFolderListWithSessionsPagination(paginationParams: { perPage: number, currentPage: number, orderBy: string }): Promise<FoldersWithPagination> {
  const { perPage, currentPage, orderBy } = paginationParams;
  const paginationQuery = `?perPage=${perPage}&currentPage=${currentPage}&orderBy=${orderBy}`

  try {
    const mpResponse = await makeMPServerRequest(
      "self/folders/list" + paginationQuery,
      "GET",
      "json",
    );

    if (mpResponse.status === "success" && mpResponse.meta) {
      return {
        folders: mpResponse.data,
        pagination: mpResponse.meta.pagination,
      } as FoldersWithPagination;  
    } else {
      throw new Error(
        `Unexpected response while getting folder list: ${JSON.stringify(mpResponse)}`,
      );
    }
  } catch (error) {
    throw new Error(
      `Unexpected exception while getting folder list: ${error}`
    );
  }
}